import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Plus, Trash2, AlertCircle, ShieldCheck } from "lucide-react";

const EMPTY_LINK = { url: "", platform: "", open_dm: false };
const localeMap = { no: "nb-NO", sv: "sv-SE", da: "da-DK", en: "en-GB" };

export default function BliSkaper() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { user, refresh: refreshAuth } = useAuth();
  const [me, setMe] = useState(user || null);
  const [existing, setExisting] = useState(null);
  const [links, setLinks] = useState([{ ...EMPTY_LINK }]);
  const [platforms, setPlatforms] = useState([]);
  const [autoApproval, setAutoApproval] = useState({ active: false, until: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user.is_creator) {
      nav(`/skaper/${user.creator_handle || user.username}`, { replace: true });
    }
  }, [user, nav]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => nav("/?tab=login"));
    api.get("/me/creator-application").then((r) => setExisting(r.data?.application || null)).catch(() => {});
    api.get("/creator-application/platforms").then((r) => {
      setPlatforms(r.data?.platforms || []);
      setAutoApproval({
        active: !!r.data?.auto_approval_active,
        until: r.data?.auto_approval_until || null,
      });
    }).catch(() => {});
  }, [nav]);

  const updateLink = (idx, patch) => {
    setLinks((curr) => curr.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addLink = () => {
    if (links.length >= 5) return;
    setLinks((curr) => [...curr, { ...EMPTY_LINK }]);
  };
  const removeLink = (idx) => {
    setLinks((curr) => curr.filter((_, i) => i !== idx));
  };

  const strongPlatformKeys = new Set(platforms.filter((p) => p.is_strong).map((p) => p.key));
  const hasStrongLink = links.some(
    (l) => strongPlatformKeys.has(l.platform) && l.open_dm && l.url.trim().length > 4
  );
  const canSubmit = autoApproval.active
    ? true
    : (hasStrongLink && links.every((l) => l.url.trim() && l.platform));

  const submit = async () => {
    if (!autoApproval.active && !hasStrongLink) {
      toast.error(t("apply_creator.submit_strong_required"));
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/creator-applications", {
        links: links.filter((l) => l.url.trim() && l.platform).map((l) => ({
          url: l.url.trim(),
          platform: l.platform,
          open_dm: !!l.open_dm,
        })),
      });
      setExisting(r.data.application);
      if (r.data.auto_approved) {
        toast.success(t("apply_creator.submitted_auto"));
        await refreshAuth();
        setTimeout(() => nav("/min-skaperside"), 1200);
      } else {
        toast.success(t("apply_creator.submitted"));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("apply_creator.submit_err"));
    } finally {
      setSubmitting(false);
    }
  };

  if (me?.is_creator || user?.is_creator) {
    const target = me || user;
    const openSkaperside = async () => {
      await refreshAuth();
      nav(`/skaper/${target.creator_handle || target.username}`, { replace: true });
    };
    return (
      <Layout>
        <div className="panel p-12 text-center max-w-2xl mx-auto">
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-3">{t("apply_creator.already_title")}</h1>
          <p className="text-[var(--cream-soft)] mb-6">{t("apply_creator.already_desc")}</p>
          <button
            onClick={openSkaperside}
            className="inline-block px-6 py-3 bg-[var(--magenta)] text-black font-meta"
            data-testid="open-my-skaperside-btn"
          >
            {t("apply_creator.open_creator_page")}
          </button>
        </div>
      </Layout>
    );
  }

  if (existing && (existing.status === "pending" || existing.status === "code_sent")) {
    return <PendingView existing={existing} onApproved={async () => {
      await refreshAuth();
      nav("/min-skaperside", { replace: true });
    }} />;
  }

  return (
    <Layout withSidebar={false}>
      <div className="max-w-3xl mx-auto" data-testid="bli-skaper-page">
        <div className="font-meta mb-3">{t("apply_creator.label")}</div>
        <h1 className="font-fraunces italic text-4xl sm:text-5xl text-[var(--cream)] mb-4 leading-tight">
          {t("apply_creator.title")}
        </h1>
        <p className="text-[var(--cream-soft)] leading-relaxed mb-10 max-w-2xl">
          <Trans i18nKey="apply_creator.intro" components={{ strong: <strong /> }} />
        </p>

        {autoApproval.active && (
          <div className="panel p-5 mb-8 border-l-2 border-[var(--magenta)]" data-testid="auto-approval-banner">
            <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-2">
              {t("apply_creator.auto_label")}
            </div>
            <p className="text-sm text-[var(--cream)] leading-relaxed mb-2">
              {t("apply_creator.auto_body")}
            </p>
            <p className="text-xs text-[var(--cream-soft)] leading-relaxed">
              <Trans
                i18nKey="apply_creator.auto_until"
                values={{
                  date: autoApproval.until && new Date(autoApproval.until).toLocaleString(localeMap[i18n.language] || "nb-NO", {
                    day: "2-digit", month: "long", year: "numeric",
                  }),
                }}
                components={{ strong: <strong className="text-[var(--cream)]" /> }}
              />
            </p>
          </div>
        )}

        {existing?.status === "rejected" && (
          <div className="panel-tight p-4 mb-8 border-l-2 border-[var(--magenta)]">
            <div className="font-meta text-[var(--magenta)] mb-1">{t("apply_creator.rejected_label")}</div>
            <p className="text-sm text-[var(--cream-soft)]">{existing.reject_reason || t("apply_creator.rejected_no_reason")}</p>
            <p className="text-xs text-[var(--cream-faint)] mt-1">{t("apply_creator.rejected_can_reapply")}</p>
          </div>
        )}

        <div className="space-y-10">
          <section>
            <div className="flex items-baseline justify-between mb-3">
              <label className="font-meta text-[var(--cream-soft)]">
                {autoApproval.active ? t("apply_creator.links_label_optional") : t("apply_creator.links_label_required")}
              </label>
              {!autoApproval.active && (
                <span className={`font-meta text-xs ${hasStrongLink ? "text-emerald-400" : "text-[var(--magenta)]"}`}>
                  {hasStrongLink ? (
                    <><ShieldCheck className="h-3 w-3 inline mr-1" /> {t("apply_creator.verified_ok")}</>
                  ) : (
                    <><AlertCircle className="h-3 w-3 inline mr-1" /> {t("apply_creator.verified_missing")}</>
                  )}
                </span>
              )}
            </div>
            <p className="text-xs text-[var(--cream-soft)] leading-relaxed mb-5">
              <Trans
                i18nKey={autoApproval.active ? "apply_creator.lead_optional" : "apply_creator.lead_required"}
                components={{ strong: <strong className="text-[var(--cream)]" /> }}
              />
            </p>

            {!autoApproval.active && (
              <div
                className="border-l-4 border-amber-500 bg-amber-500/10 p-4 mb-5"
                data-testid="apply-dm-warning"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-[var(--cream)] leading-relaxed">
                    <strong className="block mb-1.5">Viktig — verifisering krever gratis DM</strong>
                    Vi må sende deg en kode på <strong>én av plattformene du oppgir under</strong>
                    (OnlyFans, Fansly, ManyVids o.l.). Sjekk at vi kan sende deg melding
                    <strong> uten å betale for abonnement</strong> på minst én av profilene —
                    ellers får vi ikke verifisert at du er deg.
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {links.map((link, idx) => (
                <LinkRow
                  key={idx}
                  link={link}
                  platforms={platforms}
                  onChange={(patch) => updateLink(idx, patch)}
                  onRemove={() => removeLink(idx)}
                  canRemove={links.length > 1}
                  idx={idx}
                />
              ))}
            </div>

            {links.length < 5 && (
              <button onClick={addLink}
                className="btn-ghost mt-3 flex items-center gap-2"
                data-testid="apply-add-link">
                <Plus className="h-3.5 w-3.5" /> {t("apply_creator.add_link", { n: links.length })}
              </button>
            )}
          </section>

          <section className="border-l-4 border-[var(--magenta)]/40 bg-[var(--magenta)]/5 p-5">
            <p className="text-sm text-[var(--cream)] leading-relaxed mb-2">
              <strong>{t("apply_creator.info_what_happens")}</strong>
            </p>
            <ul className="list-disc pl-5 text-sm text-[var(--cream-soft)] space-y-1.5 leading-relaxed">
              <li>{t("apply_creator.info_b1")}</li>
              <li>{t("apply_creator.info_b2")}</li>
              <li>{t("apply_creator.info_b3")}</li>
            </ul>
          </section>

          <button
            data-testid="apply-submit"
            onClick={submit} disabled={submitting || !canSubmit}
            className="btn-magenta w-full sm:w-auto inline-flex items-center gap-2 disabled:opacity-50"
          >
            {submitting ? t("apply_creator.submit_sending") : t("apply_creator.submit")}
          </button>
        </div>
      </div>
    </Layout>
  );
}

function LinkRow({ link, platforms, onChange, onRemove, canRemove, idx }) {
  const { t } = useTranslation();
  const isStrong = platforms.find((p) => p.key === link.platform)?.is_strong;
  return (
    <div className="border border-white/10 p-4 space-y-3" data-testid={`apply-link-${idx}`}>
      <div className="flex gap-3 items-start">
        <div className="flex-1">
          <label className="font-meta block mb-1.5 text-[var(--cream-soft)]">{t("apply_creator.url_label")}</label>
          <input
            type="url"
            value={link.url}
            onChange={(e) => onChange({ url: e.target.value })}
            placeholder="https://onlyfans.com/dittnavn"
            className="input-editorial w-full"
            data-testid={`apply-link-url-${idx}`}
          />
        </div>
        <div className="w-56">
          <label className="font-meta block mb-1.5 text-[var(--cream-soft)]">{t("apply_creator.platform")}</label>
          <select
            value={link.platform}
            onChange={(e) => onChange({ platform: e.target.value })}
            className="input-editorial w-full"
            data-testid={`apply-link-platform-${idx}`}
          >
            <option value="">{t("apply_creator.platform_choose")}</option>
            {platforms.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}{p.is_strong ? " ✓" : ""}
              </option>
            ))}
          </select>
        </div>
        {canRemove && (
          <button onClick={onRemove}
            className="mt-7 text-[var(--cream-faint)] hover:text-red-400 transition-colors"
            data-testid={`apply-link-remove-${idx}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      {link.platform && (
        <label className={`flex items-start gap-3 cursor-pointer text-sm ${isStrong ? "text-[var(--cream)]" : "text-[var(--cream-faint)]"}`}>
          <input type="checkbox"
            checked={!!link.open_dm}
            onChange={(e) => onChange({ open_dm: e.target.checked })}
            className="mt-0.5 h-4 w-4 accent-[var(--magenta)] cursor-pointer"
            data-testid={`apply-link-dm-${idx}`}
          />
          <span>
            {t("apply_creator.dm_consent")}
            {!isStrong && (
              <span className="block text-xs text-[var(--cream-faint)] mt-0.5">
                {t("apply_creator.weak_platform_note")}
              </span>
            )}
          </span>
        </label>
      )}
    </div>
  );
}

function PendingView({ existing, onApproved }) {
  const { t, i18n } = useTranslation();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const isCodeStage = existing.status === "code_sent";

  const submitCode = async () => {
    if (!/^\d{6}$/.test(code)) {
      toast.error(t("apply_creator.pv_enter_6_digits"));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/me/creator-application/verify-code", { code });
      if (r.data?.status === "approved") {
        toast.success(t("apply_creator.pv_now_creator"));
        onApproved && onApproved();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("apply_creator.pv_wrong_code"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto panel p-12 text-center">
        <div className="font-meta text-[var(--gold)] mb-4">
          {isCodeStage ? t("apply_creator.pv_code_sent_label") : t("apply_creator.pv_received_label")}
        </div>
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-3">
          {isCodeStage ? t("apply_creator.pv_code_title") : t("apply_creator.pv_received_title")}
        </h1>

        {isCodeStage ? (
          <>
            <p className="text-[var(--cream-soft)] mb-6">
              {t("apply_creator.pv_code_desc")}
            </p>
            <div className="max-w-xs mx-auto space-y-3">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="••••••"
                inputMode="numeric"
                maxLength={6}
                className="input-editorial w-full text-center font-mono text-2xl tracking-[0.5em]"
                data-testid="creator-code-input"
              />
              <button onClick={submitCode}
                disabled={busy || code.length !== 6}
                className="btn-magenta w-full disabled:opacity-50"
                data-testid="creator-code-submit">
                {busy ? t("apply_creator.pv_confirming") : t("apply_creator.pv_confirm_code")}
              </button>
            </div>
            <p className="text-[var(--cream-faint)] text-xs mt-6">
              {t("apply_creator.pv_code_help")}
            </p>
          </>
        ) : (
          <>
            <p className="text-[var(--cream-soft)]">
              {t("apply_creator.pv_received_desc")}
            </p>
            <p className="text-[var(--cream-faint)] text-xs mt-6">
              {t("apply_creator.pv_sent_on", {
                date: new Date(existing.created_at).toLocaleDateString(localeMap[i18n.language] || "nb-NO")
              })}
            </p>
          </>
        )}
      </div>
    </Layout>
  );
}
